import type { Laterality, PlannedRoutine } from "@akina/domain";
import { type SharedFormatMessage, useFetchExerciseDetails } from "@akina/infra";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useTPMEditorStore } from "../store/useTherapyPlanManagementEditorStore";
import { useTherapyPlanManagementViewStore } from "../store/useTherapyPlanManagementViewStore";
import { twMerge } from "tailwind-merge";
import { getPhaseKeyForSession } from "../utils";
import { Button, Icon, Text, Tooltip } from "../../atoms";
import type { RoutineInformationProps } from "../therapyPlanManagementTypes";
import { TPM_COPY_PREFIX } from "../therapyPlanManagementTypes";
import { ExerciseThumbnail as RoutineThumbnail } from "./ExerciseThumbnail";
import { ExerciseName } from "./Exercise";
import { DifficultyTag } from "../../DifficultyTag";
import { formatExerciseNameWithLaterality, parseSerializedTime } from "@akina/utils";

const BASE_ROUTINE_CLASSES = "relative grid w-full grid-rows-[120px_132px] rounded-xl shadow-gray-800/10 transition-[box-shadow,background-color] transition-shadow exercise-card-shadow";
const BORDER_ACTIVE_COLOR = "shadow-[inset_0_0_1px_2px_var(--color-primary-100),0_0_1px_2px_var(--color-primary-100)]";

export function Routine(params: Readonly<{
    routine: PlannedRoutine;
    order: number;
    sessionKey: string;
    deleteRoutine: (e: React.MouseEvent<HTMLButtonElement>, key: string) => void;
    disableDelete?: boolean;
    formatMessage: SharedFormatMessage;
}>) {
    const { routine, order, formatMessage } = params;
    const isEditing = useTPMEditorStore(state => state.isEditing);

    const { attributes, listeners, setNodeRef, transform, transition, isDragging, active } = useSortable({
        id: routine.key,
        disabled: !isEditing,
    });

    const style = { transform: CSS.Transform.toString(transform), transition };
    const { data: details } = useFetchExerciseDetails(routine.exerciseTemplateKey);

    const routineSetAndBreaksInfo = {
        nSet: routine.nSet,
        nRepetitions: routine.nRepetitions ?? 0,
        duration: routine.duration ?? "0S",
        breakDuration: routine.breakDuration ?? "0S",
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
            tabIndex={-1}
            data-routine-key={routine.key}
            className={twMerge(
                BASE_ROUTINE_CLASSES,
                isEditing && "cursor-grab",
                isDragging && BORDER_ACTIVE_COLOR,
            )}>
            <RoutineOrderNumber order={order} isDragging={isDragging} />
            <RoutineThumbnail thumbnail={details?.imageUrl} previewGif={details?.previewGif} />
            <RoutineInformation
                formatMessage={formatMessage}
                className="bg-white"
                exerciseName={details?.name ?? ""}
                difficulty={routine?.difficultyLevel ?? null}
                routineSetAndBreaksInfo={routineSetAndBreaksInfo}
                routineChosenLaterality={routine.hasLaterality ? routine.laterality : null}
                actions={isEditing && !active ? (
                    <RoutineActions
                        routine={routine}
                        sessionKey={params.sessionKey}
                        exerciseName={details?.name ?? ""}
                        deleteRoutine={params.deleteRoutine}
                        disableDelete={params.disableDelete}
                        formatMessage={formatMessage}
                    />
                ) : null}
            />
        </div>
    );
}

function RoutineOrderNumber(params: Readonly<{ order: number; isDragging: boolean }>) {
    const { order, isDragging } = params;
    return (
        <div
            className={twMerge(
                "absolute top-2 left-2 z-10 flex h-6 w-6 items-center justify-center rounded-full bg-white transition-colors",
                isDragging && "bg-primary-100",
            )}>
            <Text
                size="sm"
                fontWeight="font-extrabold"
                className="-ml-px"
                color={isDragging ? "text-white" : "text-gray-800/70"}>
                {order}
            </Text>
        </div>
    );
}

export function RoutineInformation(params: Readonly<RoutineInformationProps>) {
    const { exerciseName, difficulty, actions, routineSetAndBreaksInfo, routineChosenLaterality, className, showLateralityInDetails, formatMessage } = params;
    const isEditing = useTPMEditorStore(state => state.isEditing);
    const formattedExerciseName = formatExerciseNameWithLaterality(
        exerciseName,
        routineChosenLaterality ?? null,
        formatMessage,
    );

    return (
        <div
            className={twMerge(
                "flex h-full bg-transparent flex-1 flex-col gap-2 overflow-hidden rounded-b-xl p-4",
                className,
            )}>
            <div className="flex items-center gap-2">
                <DifficultyTag difficulty={difficulty} message={formatMessage({ id: `shared.difficulty.${difficulty ?? "custom"}` })} />
                <div className="flex-1" />
                {isEditing ? actions : null}
            </div>
            <ExerciseName name={formattedExerciseName ?? exerciseName} className="bg-primary-800/10" />
            <RoutineSetAndBreaks
                {...routineSetAndBreaksInfo}
                laterality={showLateralityInDetails ? routineChosenLaterality : null}
                fm={formatMessage}
            />
        </div>
    );
}

function RoutineActions(params: Readonly<{
    routine: PlannedRoutine;
    sessionKey: string;
    exerciseName: string;
    deleteRoutine: (e: React.MouseEvent<HTMLButtonElement>, key: string) => void;
    disableDelete?: boolean;
    formatMessage: SharedFormatMessage;
}>) {
    const { routine, sessionKey, exerciseName, deleteRoutine, disableDelete, formatMessage } = params;
    const sessions = useTherapyPlanManagementViewStore(state => state.data?.sessions);
    const { setSessionKey, setEditRoutineParams, setModalOpen } = useTPMEditorStore(state => state.actions);
    const setPhaseKey = useTherapyPlanManagementViewStore(state => state.actions.setPhaseKey);

    const currentSession = sessions?.find(s => s.key === sessionKey);
    const currentRoutines = currentSession?.routinePlans ?? [];

    const handleEdit = () => {
        const data = useTherapyPlanManagementViewStore.getState().data;
        const phaseKey = getPhaseKeyForSession(sessionKey, data);
        setSessionKey(sessionKey);
        if (phaseKey) setPhaseKey(phaseKey);
        setEditRoutineParams({ routine, exerciseName, selectedRoutines: currentRoutines });
        setModalOpen("editRoutine");
    };

    return (
        <>
            <Button variant="icon" onClick={handleEdit}>
                <Icon type="edit" sizeClasses="h-4 w-4" />
            </Button>
            {disableDelete ? (
                <Tooltip side="bottom" trigger={
                    <Button variant="icon" disabled data-testid={`routine-delete-button-${routine.key}`}>
                        <Icon type="deleteOutlined" sizeClasses="h-4 w-4" />
                    </Button>
                }>
                    {formatMessage({ id: `${TPM_COPY_PREFIX}.disabledCancelTooltip` })}
                </Tooltip>
            ) : (
                <Button variant="icon" onClick={e => deleteRoutine(e, routine.key)} data-testid={`routine-delete-button-${routine.key}`}>
                    <Icon type="deleteOutlined" sizeClasses="h-4 w-4" />
                </Button>
            )}
        </>
    );
}

function formatRoutineDisplay(params: {
    nSet: number;
    isRepBased?: boolean;
    nRepetitions?: number;
    duration?: string;
    breakDuration: string;
    laterality?: Laterality | null;
    fm: SharedFormatMessage;
}): string {
    const { nSet, isRepBased, nRepetitions, duration, breakDuration, laterality, fm } = params;
    const repNumKey = `${TPM_COPY_PREFIX}.routineRepNum`;
    const repTimeKey = `${TPM_COPY_PREFIX}.routineRepTime`;
    const breakDurationKey = `${TPM_COPY_PREFIX}.routineBreakDuration`;

    const isRepBasedRoutine = (isRepBased !== undefined && isRepBased) || (nRepetitions !== undefined && nRepetitions > 0);
    const repNumText = fm({ id: repNumKey });
    const repTimeText = fm({ id: repTimeKey });

    const effortValue = isRepBasedRoutine ? (nRepetitions ?? 0) : parseSerializedTime(duration ?? "0s");
    const effortLabel = isRepBasedRoutine ? repNumText : repTimeText;
    const effortText = `${effortValue} ${effortLabel}`;

    const breakText = fm({ id: breakDurationKey }, { seconds: parseSerializedTime(breakDuration) });
    const lateralityText = getLateralityText(laterality, fm);

    const lateralitySuffix = lateralityText ? ` · ${lateralityText}` : "";

    return `${nSet}x${effortText} · ${breakText}${lateralitySuffix}`;
}

function RoutineSetAndBreaks(params: Readonly<{
    nSet: number;
    isRepBased?: boolean;
    nRepetitions?: number;
    duration?: string;
    breakDuration: string;
    laterality?: Laterality | null;
    fm: SharedFormatMessage;
}>) {
    return (
        <div className="flex items-center gap-2 justify-self-end">
            <Text size="xs" color="text-gray-800/90" className="leading-4">
                {formatRoutineDisplay(params)}
            </Text>
        </div>
    );
}

function getLateralityText(laterality: Laterality | null | undefined, fm: SharedFormatMessage) {
    if (!laterality) return null;
    switch (laterality) {
        case "both": return fm({ id: `${TPM_COPY_PREFIX}.bothSidesLabel` });
        case "left": return fm({ id: `${TPM_COPY_PREFIX}.leftLabel` });
        case "right": return fm({ id: `${TPM_COPY_PREFIX}.rightLabel` });
        default: return null;
    }
}